import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  width: 100%;
  min-height: calc(100vh - 80px);
  padding: 16px 16px 100px 16px;
  box-sizing: border-box;
  color: #ffffff;
`;

const PageTitle = styled.h1`
  font-size: 20px;
  margin: 0 0 12px 0;
  letter-spacing: 0.4px;
`;

const Card = styled.div`
  background: rgba(20, 20, 25, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SettingLink = styled.a`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: #ffffff;
  text-decoration: none;
  font-size: 16px;
  font-weight: 600;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  &:active {
    transform: scale(0.99);
  }
`;

const SettingButton = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  &:active {
    transform: scale(0.99);
  }
`;

const SettingIcon = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 360px;
  background: #0b0b0f;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 18px;
  cursor: pointer;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 14px;
`;

const ModalButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border-radius: 14px;
  border: 1px solid rgba(16, 185, 129, 0.4);
  background: rgba(16, 185, 129, 0.14);
  color: #10b981;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
`;

const HelperText = styled.p`
  margin: 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #f87171;
`;

const APE_SETTINGS_KEY = 'pwa_ape_amount_sol';

const SentryDocsPWA: React.FC = () => {
  const [apeModalOpen, setApeModalOpen] = useState(false);
  const [apeAmount, setApeAmount] = useState('');
  const [apeStatus, setApeStatus] = useState<string | null>(null);
  const [apeError, setApeError] = useState<string | null>(null);

  useEffect(() => {
    if (!apeModalOpen) return;
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(APE_SETTINGS_KEY);
    if (stored) {
      setApeAmount(stored);
    }
  }, [apeModalOpen]);

  const items = [
    {
      label: 'Ape Settings',
      icon: '/ape-settings.png',
      action: () => {
        setApeError(null);
        setApeStatus(null);
        setApeModalOpen(true);
      }
    },
    {
      label: 'User Guide',
      href: 'https://mavrk.gitbook.io/sentry-docs',
      icon: '/settings-guide.png'
    },
    {
      label: 'Sentry Token',
      href: 'https://mavrk.gitbook.io/sentry-docs/sentry-token/sentry-token-sentry',
      icon: '/settings-sentry-token.png'
    },
    {
      label: 'Ecosystem Economic Flywheel',
      href: 'https://mavrk.gitbook.io/sentry-docs/flywheel/sentry-ecosystem-economic-flywheel',
      icon: '/settings-flywheel.png'
    },
    {
      label: 'User Agreement',
      href: 'https://mavrk.gitbook.io/sentry-docs/flywheel/user-agreement',
      icon: '/settings-user-agreement.png'
    },
    {
      label: 'Contact Us',
      href: 'https://t.me/deployerone',
      icon: '/settings-contact.png'
    },
    {
      label: 'Follow Sentry on X',
      href: 'https://x.com/sentrylauncher',
      icon: '/settings-follow-on-x.png'
    },
    {
      label: 'Join the Sentry Telegram',
      href: 'https://t.me/sentrylauncher',
      icon: '/settings-telegram.png'
    }
  ];

  return (
    <PageContainer>
      <PageTitle>Settings</PageTitle>
      <Card>
        {items.map((item) => (
          item.action ? (
            <SettingButton key={item.label} type="button" onClick={item.action}>
              <SettingIcon src={item.icon} alt="" aria-hidden="true" />
              {item.label}
            </SettingButton>
          ) : (
            <SettingLink key={item.label} href={item.href} target="_blank" rel="noreferrer">
              <SettingIcon src={item.icon} alt="" aria-hidden="true" />
              {item.label}
            </SettingLink>
          )
        ))}
      </Card>
      {apeModalOpen && (
        <ModalBackdrop onClick={() => setApeModalOpen(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Ape Settings</ModalTitle>
              <CloseButton type="button" onClick={() => setApeModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            <HelperText>Set your one-click market buy amount (SOL). Slippage is fixed at 0.5%.</HelperText>
            <ModalInput
              type="number"
              min="0"
              step="0.01"
              placeholder="0.5"
              value={apeAmount}
              onChange={(event) => setApeAmount(event.target.value)}
            />
            {apeError && <ErrorText>{apeError}</ErrorText>}
            {apeStatus && <HelperText>{apeStatus}</HelperText>}
            <ModalButton
              type="button"
              onClick={() => {
                const numeric = Number(apeAmount);
                if (!Number.isFinite(numeric) || numeric <= 0) {
                  setApeError('Enter a valid SOL amount greater than 0.');
                  return;
                }
                if (typeof window !== 'undefined') {
                  window.localStorage.setItem(APE_SETTINGS_KEY, numeric.toString());
                }
                setApeError(null);
                setApeStatus('Ape settings saved.');
              }}
            >
              Save
            </ModalButton>
          </ModalCard>
        </ModalBackdrop>
      )}
    </PageContainer>
  );
};

export default SentryDocsPWA;
